import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { getInwardStockListAction, deleteInwardStockAction } from "../../../store/actions/inwardStockAction";

const InwardStockList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get data from Redux store
  const { inwardStocks = [], loading = false } = useSelector((state) => state.inwardStock || {});

  // Search and pagination states
  const [searchQueries, setSearchQueries] = useState({
    branch: "",
    batchNo: "",
    qtyPerCarton: "",
  });
  const [sortField, setSortField] = useState("branch");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState("");
  const rowsPerPage = 10;

  // Fetch inward stock list on component mount
  useEffect(() => {
    const fetchData = async () => {
      setError("");
      try {
        await dispatch(getInwardStockListAction());
      } catch (err) {
        console.error("Failed to fetch inward stock list:", err);
        setError("Failed to load inward stock records. Please try again.");
      }
    };
    fetchData();
  }, [dispatch]);

  // Flatten data for search/sort/pagination based on API response structure
  const flattenedData = [];
  if (Array.isArray(inwardStocks) && inwardStocks.length > 0) {
    inwardStocks.forEach((item) => {
      flattenedData.push({
        id: item.inward_id,
        branch: item.branch_name,
        invoiceNo: item.invoice_no,
        batchNo: item.total_medicine,
        qtyPerCarton: item.total_qty,
        inwardId: item.inward_id,
        createdAt: item.created_at,
      });
    });
  }

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    const clearedQueries = {
      branch: "",
      batchNo: "",
      qtyPerCarton: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);
  };
  
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);
  };

  // Filter data
  const filteredData = flattenedData.filter((item) =>
    Object.keys(searchQueries).every((key) => {
      const searchValue = searchQueries[key].toLowerCase();
      const itemValue = item[key]?.toString().toLowerCase() || "";
      return searchValue === "" || itemValue.includes(searchValue);
    })
  );

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    const modifier = sortOrder === "asc" ? 1 : -1;
    if (a[sortField] < b[sortField]) return -1 * modifier;
    if (a[sortField] > b[sortField]) return 1 * modifier;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  // View inward details - pass inward_id to navigate
  const handleViewDetails = (item) => {
    navigate("/app-add-inward-stock", { 
      state: { 
        inward_id: item.inwardId,
        isViewMode: true
      } 
    });
  };

  // Delete function with SweetAlert textarea for reason
  const handleDelete = (item) => {
    let reasonText = "";
  
    swal({
      title: "Delete Inward Stock",
      text: `You are about to delete inward stock for ${item.branch}`,
      icon: "warning",
      content: {
        element: "textarea",
        attributes: {
          placeholder: "Please provide a reason for deletion...",
          value: "",
          style:
            "width: 100%; height: 100px; padding: 10px; border: 1px solid #ddd; border-radius: 5px;",
        },
      },
      buttons: true, // ✅ use default buttons
      dangerMode: true,
    }).then(async (result) => {
      const textarea = document.querySelector("textarea");
      reasonText = textarea ? textarea.value : "";
  
      if (!result) {
        swal("Cancelled", "Your record is safe!", "error");
        return;
      }
  
      if (!reasonText.trim()) {
        swal("Error", "Please provide a reason for deletion.", "error");
        return;
      }
  
      try {
        await dispatch(
          deleteInwardStockAction({
            inward_id: item.inwardId,
            reason: reasonText,
          })
        );
  
    
  
        await dispatch(getInwardStockListAction());
      } catch (error) {
        console.error("Delete failed:", error);
        swal("Error", "Failed to delete inward stock", "error");
      }
    });
  };

  return (
    <>
      {/* Add Button Row */}
      <div className="row mb-3">
        <div className="col-12 text-end">
          <button
            className="btn btn-primary btn-rounded"
            onClick={() => navigate("/app-add-inward-stock")}
          >
            + Add Inward Stock
          </button>
        </div>
      </div>

      {/* Card Layout */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("branch")} style={{ cursor: "pointer" }}>
                            Branch {sortField === "branch" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              placeholder="Search branch"
                              value={searchQueries.branch}
                              onChange={(e) => handleSearch(e, "branch")}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          
                          <th onClick={() => handleSort("batchNo")} style={{ cursor: "pointer" }}>
                            Total Medicines {sortField === "batchNo" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              placeholder="Search total medicines"
                              value={searchQueries.batchNo}
                              onChange={(e) => handleSearch(e, "batchNo")}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("qtyPerCarton")} style={{ cursor: "pointer" }}>
                            Total Qty {sortField === "qtyPerCarton" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              placeholder="Search quantity"
                              value={searchQueries.qtyPerCarton}
                              onChange={(e) => handleSearch(e, "qtyPerCarton")}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((item, idx) => (
                            <tr key={item.id || idx}>
                              <td>{item.branch}</td>
                         
                              <td>{item.batchNo}</td>
                              <td>{item.qtyPerCarton}</td>
                              <td className="text-center align-middle">
                                <div className="d-inline-flex gap-3">
                                  <i
                                    className="fas fa-eye text-info"
                                    style={{ cursor: "pointer", fontSize: "16px" }}
                                    onClick={() => handleViewDetails(item)}
                                    title="View Details"
                                  ></i>
                                  <i
                                    className="fas fa-trash text-danger"
                                    style={{ cursor: "pointer", fontSize: "16px" }}
                                    onClick={() => handleDelete(item)}
                                    title="Delete"
                                  ></i>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-4">
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {sortedData.length > 0 && (
                    <div className="pagination-wrapper" style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: "center", marginTop: "20px", flexWrap: "wrap", flexDirection: "column" }}>
                      <div>
                        Showing {paginatedData?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                        {Math.min((currentPage + 1) * rowsPerPage, sortedData.length)} of {sortedData.length} entries
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                        <button onClick={() => handlePageChange(Math.max(currentPage - 1, 0))} disabled={currentPage === 0} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: currentPage === 0 ? "not-allowed" : "pointer", opacity: currentPage === 0 ? 0.5 : 1 }}>&lt;</button>
                        {(() => {
                          const pages = [];
                          const maxPagesToShow = 5;
                          const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                          const endPage = Math.min(startPage + maxPagesToShow, totalPages);
                          if (startPage > 0) {
                            pages.push(<button key={0} onClick={() => handlePageChange(0)} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: "pointer" }}>1</button>);
                            if (startPage > 1) pages.push(<span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>...</span>);
                          }
                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i;
                            pages.push(<button key={i} onClick={() => handlePageChange(i)} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: isActive ? "#007bff" : "#fff", color: isActive ? "#fff" : "#000", cursor: "pointer" }}>{i + 1}</button>);
                          }
                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) pages.push(<span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>...</span>);
                            pages.push(<button key={totalPages - 1} onClick={() => handlePageChange(totalPages - 1)} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: "pointer" }}>{totalPages}</button>);
                          }
                          return pages;
                        })()}
                        <button onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))} disabled={currentPage === totalPages - 1} style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "#fff", color: "#000", cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer", opacity: currentPage === totalPages - 1 ? 0.5 : 1 }}>&gt;</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InwardStockList;